import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    CardContent,
    CardMedia,
    Typography,
    IconButton,
    Stack,
    Grid,
    Tooltip,
    Snackbar,
} from '@mui/material';
import {
    PlayArrow,
    ThumbUp,
    Chat,
    Visibility,
    CalendarToday,
    ContentCopy,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { OriginalSponsorMentions, SponsorMention } from './OriginalSponsorMentions';
import { formatChileanDate } from '../utils/dateUtils';
import ProgressiveImage from './common/ProgressiveImage';

interface Sponsor {
    _id: string;
    name: string;
    logo?: string;
    website?: string;
    category?: string;
    mentionCount: number;
    episodeCount: number;
    totalViews?: number;
}

interface Episode {
    _id: string;
    title: string;
    description?: string;
    duration?: number;
    publishedAt?: string;
    podcastId: string;
    sponsors?: Sponsor[];
    thumbnail?: string;
    url?: string;
    views?: string;
    likes?: string;
    comments?: string;
    source?: string;
}

interface Podcast {
    _id: string;
    title: string;
    imageUrl?: string;
    youtubeData?: {
        snippet?: {
            thumbnails?: {
                high?: {
                    url: string;
                };
            };
        };
    };
}

interface EpisodeCardProps {
    episode: Episode;
    podcast: Podcast;
    selectedSponsors?: Sponsor[];
}

export const EpisodeCard: React.FC<EpisodeCardProps> = ({
    episode,
    podcast,
    selectedSponsors = [],
}) => {
    const [showCopiedSnackbar, setShowCopiedSnackbar] = useState(false);

    const { data: sponsorMentions } = useQuery<SponsorMention[]>({
        queryKey: ['episode-sponsors', episode._id],
        queryFn: () => apiService.getEpisodeSponsorMentions(episode._id) as unknown as Promise<SponsorMention[]>,
        enabled: !!episode._id,
    });

    const filteredMentions = useMemo(() => {
        if (!sponsorMentions) return [];

        return sponsorMentions.filter((mention: SponsorMention) => {
            // If no filters are selected, show all mentions
            if (selectedSponsors.length === 0) {
                return true;
            }

            // Filter by sponsor names if any are selected
            const selectedSponsorsNames = selectedSponsors.map(
                (sponsor: Sponsor) => sponsor.name
            );
            if (
                selectedSponsors.length > 0 &&
                !selectedSponsorsNames.includes(mention.sponsor.name)
            ) {
                return false;
            }

            return true;
        });
    }, [sponsorMentions, selectedSponsors]);

    const thumbnailUrl =
        episode.thumbnail || podcast.youtubeData?.snippet?.thumbnails?.high?.url || '';

    const handleCopyId = () => {
        if (episode._id) {
            navigator.clipboard.writeText(episode._id);
            setShowCopiedSnackbar(true);
        }
    };

    return (
        <Grid container sx={{ p: 0, minHeight: '120px' }}>
            <Grid
                size={{ xs: 12, sm: 4, md: 3, lg: 2 }}
                sx={{
                    position: 'relative',
                    minHeight: { md: '120px' },
                }}
            >
                <Box sx={{ position: 'relative', p: 1 }}>
                    <ProgressiveImage
                        src={thumbnailUrl}
                        alt={episode.title}
                        sx={{
                            height: '100%',
                            minWidth: '120px',
                            objectFit: 'cover',
                            borderRadius: 2,
                        }}
                    />
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            bgcolor: 'primary.main',
                            color: 'white',
                            width: 40,
                            height: 40,
                            '&:hover': { bgcolor: 'primary.dark', opacity: 0.8 },
                        }}
                        component="a"
                        href={episode.url}
                        target="_blank"
                    >
                        <PlayArrow sx={{ fontSize: 30 }} />
                    </IconButton>
                </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 8, md: 9, lg: 10 }}>
                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: '100%',
                    }}
                >
                    <Typography component="div" variant="h6" sx={{ fontWeight: 600 }}>
                        {episode.title}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                        {episode.source === 'youtube' && (
                            <>
                                <Tooltip title="Número de veces que este episodio ha sido visto">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                        <Visibility
                                            sx={{
                                                fontSize: 16,
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                        {Number(episode.views)?.toLocaleString()}
                                    </Typography>
                                </Tooltip>
                                {episode.likes && (
                                    <Tooltip title="Número de me gusta que este episodio ha recibido">
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <ThumbUp sx={{ fontSize: 16 }} />{' '}
                                            {Number(episode.likes)?.toLocaleString()}
                                        </Typography>
                                    </Tooltip>
                                )}
                                {episode.comments && (
                                    <Tooltip title="Número de comentarios en este episodio">
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Chat sx={{ fontSize: 16 }} />{' '}
                                            {Number(episode.comments)?.toLocaleString()}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </>
                        )}
                        {process.env.NODE_ENV === 'development' && (
                            <Tooltip title="Copy ID">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            '&:hover': { color: 'primary.main' },
                                        }}
                                        onClick={handleCopyId}
                                    >
                                        {episode._id}
                                    </Typography>
                                    <IconButton size="small" onClick={handleCopyId}>
                                        <ContentCopy sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        )}
                        <Tooltip title="Fecha de publicación">
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <CalendarToday sx={{ fontSize: 14 }} />
                                {formatChileanDate(episode.publishedAt)}
                            </Typography>
                        </Tooltip>
                    </Stack>
                    {sponsorMentions && (
                        <Box sx={{ mt: 1 }}>
                            <OriginalSponsorMentions
                                mentions={filteredMentions}
                                onTimeClick={time => {
                                    window.open(`${episode.url}&t=${time}s`, '_blank');
                                }}
                                avatarSize={32}
                            />
                        </Box>
                    )}
                </CardContent>
            </Grid>
        </Grid>
    );
}; 